import { useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Modal } from '@safetyhub/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeviceLayout } from '../../../../hooks/useDeviceLayout';
import { ChevronRightIcon } from './Icons';
import {
  getSiteLocations,
  getSiteDetailLocations,
  getSiteEquipment,
  getSiteProcessTypes,
  createSiteEquipment,
  createSiteLocation,
  createSiteDetailLocation,
  createSiteProcessType,
  updateSiteEquipment,
  flattenProcessTypeHierarchy,
  SiteEquipment,
} from '../../../../lib/site-metadata';
// `extractApiError(...).message`가 아니라 `apiErrorMessage`다 — 네트워크·알 수 없는 오류에서 axios 원문
// (「Network Error」·「Request failed with status code 502」)이 화면에 새지 않게(리뷰 1103 F7)
import { apiErrorMessage } from '../../../../lib/api';
import { SearchableSelect } from '../../../../components/inspection/shared/SearchableSelect';
import { DirtyHint } from '../../../../components/common/DirtyHint';
import { formatDateISO, objectParticleOf, parseDateLocalToUTC } from '@safetyhub/shared';
import { useLegalDocumentsVisibility } from '../../../../hooks/useLegalDocumentsVisibility';
import {
  getSiteEquipmentLegal,
  regulatedEquipmentQueryKeys,
  setSiteEquipmentReview,
} from '../../../../lib/regulatedEquipment';
import { RegulatedEquipmentBadges, RegulatedEquipmentFields } from './RegulatedEquipmentFields';

interface Props {
  companyId: string;
  siteId: string;
  isOwner: boolean;
}

/**
 * 수정 dirty 판정 스냅샷 — 폼이 노출하는 **입력 전수**(대상명·관리번호·공종/공정·장소·위치).
 * 하나라도 빠뜨리면 "바꿨는데 저장이 안 눌리는" 반대 방향 결함이 되므로 폼 본문과 1:1로 맞춘다.
 * 폼이 수정 클릭 핸들러에서 열리고 그 시점에 원본이 손에 있어, 기준점도 거기서 만든다.
 */
function equipmentFormSnapshot(form: {
  name: string;
  managementNumber: string;
  processTypeId: string;
  locationId: string;
  detailLocationId: string;
  /** 법정 칸(②-c2) — 기종·규격 값·설치일·배기 공정도 폼 입력이라 스냅샷에 든다 */
  kindCode: string;
  specValues: Record<string, unknown>;
  installedDate: string;
  exhaustProcessIds: readonly string[];
}): string {
  return JSON.stringify({
    name: form.name.trim(),
    managementNumber: form.managementNumber.trim(),
    processTypeId: form.processTypeId,
    locationId: form.locationId,
    detailLocationId: form.detailLocationId,
    kindCode: form.kindCode,
    // 키 순서가 달라도 같은 값이면 같다 — 입력 순서로 dirty가 나지 않게 정렬한다
    specValues: Object.keys(form.specValues)
      .sort()
      .map((k) => [k, form.specValues[k]]),
    installedDate: form.installedDate,
    exhaustProcessIds: [...form.exhaustProcessIds].sort(),
  });
}

/** 서버의 설치일(ISO) → 날짜 입력 값(`YYYY-MM-DD` · 기본 시간대) */
function toDateInput(iso: string | null | undefined): string {
  return iso ? formatDateISO(new Date(iso)) : '';
}

export function SiteEquipmentSection({ companyId, siteId, isOwner }: Props) {
  const queryClient = useQueryClient();
  const deviceLayout = useDeviceLayout();
  const isDesktop = deviceLayout === 'desktop';

  const { data: equipment = [], isLoading } = useQuery({
    queryKey: ['site-equipment', companyId, siteId],
    queryFn: () => getSiteEquipment(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['site-locations', companyId, siteId],
    queryFn: () => getSiteLocations(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });

  const { data: detailLocations = [] } = useQuery({
    queryKey: ['site-detail-locations', companyId, siteId],
    queryFn: () => getSiteDetailLocations(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });

  const { data: processTypes = [] } = useQuery({
    queryKey: ['site-process-types', companyId, siteId],
    queryFn: () => getSiteProcessTypes(companyId, siteId),
    enabled: !!companyId && !!siteId,
  });
  // 폼의 「추가」로 방금 만든 공종 id → 이름. 목록 재조회(invalidate)가 끝나기 전에 저장하면
  // `processTypes`에 그 id가 아직 없어 이름 대신 «id 문자열»이 공종 텍스트로 저장됐다(리뷰 1103).
  const createdProcessTypeNamesRef = useRef(new Map<string, string>());

  // ②-c2 — 법정 설비 칸은 법정 서류 관리(플래그 `legal-obligation`)가 보이는 조직에서만 그린다(메뉴·페이지와 같은 훅)
  const { visible: legalVisible } = useLegalDocumentsVisibility(companyId);
  const { data: legal } = useQuery({
    queryKey: regulatedEquipmentQueryKeys.legal(companyId, siteId),
    queryFn: () => getSiteEquipmentLegal(companyId, siteId),
    enabled: legalVisible && !!companyId && !!siteId,
  });
  const legalClasses = useMemo(() => legal?.classes ?? [], [legal]);
  const classOf = (code: string | null | undefined) => (code ? legalClasses.find((c) => c.code === code) : undefined);
  const [isTogglingReview, setIsTogglingReview] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const toggleReview = async () => {
    if (!legal) return;
    setIsTogglingReview(true);
    setReviewError('');
    try {
      await setSiteEquipmentReview(companyId, siteId, !legal.equipmentReviewedAt);
      queryClient.invalidateQueries({ queryKey: regulatedEquipmentQueryKeys.legal(companyId, siteId) });
    } catch (err) {
      setReviewError(apiErrorMessage(err) ?? '검토 표시를 저장하지 못했습니다.');
    } finally {
      setIsTogglingReview(false);
    }
  };

  const activeLocations = useMemo(() => locations.filter((l) => l.isActive), [locations]);
  const activeDetailLocations = useMemo(() => detailLocations.filter((l) => l.isActive), [detailLocations]);
  const processTypeOptions = useMemo(() => flattenProcessTypeHierarchy(processTypes), [processTypes]);

  // 폼 상태
  const [showModal, setShowModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<SiteEquipment | null>(null);
  const [formName, setFormName] = useState('');
  const [formManagementNumber, setFormManagementNumber] = useState('');
  const [formProcessType, setFormProcessType] = useState('');
  const [formLocationId, setFormLocationId] = useState('');
  const [formDetailLocationId, setFormDetailLocationId] = useState('');
  const [formKindCode, setFormKindCode] = useState('');
  const [formSpecValues, setFormSpecValues] = useState<Record<string, unknown>>({});
  const [formInstalledDate, setFormInstalledDate] = useState('');
  const [formExhaustProcessIds, setFormExhaustProcessIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  /** 수정 진입 시점 스냅샷 — 추가 모드는 null(게이트 비대상). */
  const [editBaseline, setEditBaseline] = useState<string | null>(null);

  // 상세 모달
  const [selectedEquipment, setSelectedEquipment] = useState<SiteEquipment | null>(null);

  // 비활성화 확인
  const [deactivateTarget, setDeactivateTarget] = useState<SiteEquipment | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  // 추가/수정 모달이 열릴 때 대상명 입력에 포커스 — `autoFocus`는 base Modal의 마운트 포커스
  // (dialog 컨테이너)에 덮이므로 initialFocusRef로 넘긴다.
  const nameInputRef = useRef<HTMLInputElement>(null);

  const activeEquipment = equipment.filter((e) => e.isActive);

  const resetForm = () => {
    setShowModal(false);
    setEditingEquipment(null);
    setFormName('');
    setFormManagementNumber('');
    setFormProcessType('');
    setFormLocationId('');
    setFormDetailLocationId('');
    setFormKindCode('');
    setFormSpecValues({});
    setFormInstalledDate('');
    setFormExhaustProcessIds([]);
    setEditBaseline(null);
    setError('');
  };

  const openAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (eq: SiteEquipment) => {
    setSelectedEquipment(null);
    setEditingEquipment(eq);
    setFormName(eq.name);
    setFormManagementNumber(eq.managementNumber ?? '');
    // 기존 processType 텍스트에 매칭되는 SiteProcessType id로 변환 (없으면 빈 문자열)
    const matchedPt = processTypes.find((pt) => pt.name === eq.processType);
    setFormProcessType(matchedPt?.id ?? '');
    setFormLocationId(eq.currentLocationId ?? '');
    setFormDetailLocationId(eq.currentDetailLocationId ?? '');
    setFormKindCode(eq.equipmentKindCode ?? '');
    setFormSpecValues(eq.specValues ?? {});
    setFormInstalledDate(toDateInput(eq.installedAt));
    setFormExhaustProcessIds(eq.exhaustProcessIds ?? []);
    // 기준점은 **폼에 채우는 값 그대로** 만든다(원본 필드가 아니라 — 공종/공정은 id로 변환되고
    // 매칭 실패 시 빈 값이라, 원본 기준으로 잡으면 열자마자 dirty가 된다).
    setEditBaseline(
      equipmentFormSnapshot({
        name: eq.name,
        managementNumber: eq.managementNumber ?? '',
        processTypeId: matchedPt?.id ?? '',
        locationId: eq.currentLocationId ?? '',
        detailLocationId: eq.currentDetailLocationId ?? '',
        kindCode: eq.equipmentKindCode ?? '',
        specValues: eq.specValues ?? {},
        installedDate: toDateInput(eq.installedAt),
        exhaustProcessIds: eq.exhaustProcessIds ?? [],
      }),
    );
    setError('');
    setShowModal(true);
  };

  // 수정 모드에서 값이 진입 시점 그대로면 저장을 막는다(추가 모드는 게이트 비대상).
  const isUnchanged =
    !!editingEquipment &&
    editBaseline !== null &&
    equipmentFormSnapshot({
      name: formName,
      managementNumber: formManagementNumber,
      processTypeId: formProcessType,
      locationId: formLocationId,
      detailLocationId: formDetailLocationId,
      kindCode: formKindCode,
      specValues: formSpecValues,
      installedDate: formInstalledDate,
      exhaustProcessIds: formExhaustProcessIds,
    }) === editBaseline;

  const handleSubmit = async () => {
    if (!formName.trim() || isUnchanged) return;
    setIsSubmitting(true);
    setError('');
    try {
      const selectedPt = processTypes.find((pt) => pt.id === formProcessType);
      // `formProcessType`은 늘 공종 «id»다(SearchableSelect 옵션·onAdd 둘 다 id) — 이름을 못 찾았을 때
      // 그 id를 텍스트로 떨어뜨리지 않는다.
      const processTypeName =
        selectedPt?.name ?? createdProcessTypeNamesRef.current.get(formProcessType) ?? null;
      const dto = {
        name: formName.trim(),
        managementNumber: formManagementNumber.trim() || null,
        processType: processTypeName,
        currentLocationId: formLocationId || null,
        currentDetailLocationId: formDetailLocationId || null,
        // 법정 칸(②-c2)은 그 칸이 보이는 조직에서만 보낸다 — 안 보이는 화면이 «비움»을 보내면 남이 넣은 값을 지운다
        ...(legalVisible && legal
          ? {
              equipmentKindCode: formKindCode || null,
              specValues: formKindCode ? formSpecValues : null,
              installedAt: formKindCode && formInstalledDate ? parseDateLocalToUTC(formInstalledDate).toISOString() : null,
              // 배기 공정 — 측정 결과 축 기종(국소배기)만 받는 칸이라 그 기종일 때만 싣는다(다른 기종은 서버가 비운다)
              ...(classOf(formKindCode)?.specFields.some((f) => f.source === 'WORK_ENVIRONMENT_MEASUREMENT')
                ? { exhaustProcessIds: formExhaustProcessIds }
                : {}),
            }
          : {}),
      };
      if (editingEquipment) {
        await updateSiteEquipment(companyId, siteId, editingEquipment.id, dto);
      } else {
        await createSiteEquipment(companyId, siteId, dto);
      }
      queryClient.invalidateQueries({ queryKey: ['site-equipment', companyId, siteId] });
      resetForm();
    } catch (err) {
      setError(apiErrorMessage(err) ?? '장비를 저장하지 못했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDeactivate = () => {
    setDeactivateTarget(null);
    setDeactivateError('');
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setIsDeactivating(true);
    setDeactivateError('');
    try {
      await updateSiteEquipment(companyId, siteId, deactivateTarget.id, { isActive: false });
      queryClient.invalidateQueries({ queryKey: ['site-equipment', companyId, siteId] });
      setDeactivateTarget(null);
    } catch (err) {
      setDeactivateError(apiErrorMessage(err) ?? '장비를 비활성화하지 못했습니다.');
    } finally {
      setIsDeactivating(false);
    }
  };

  return (
    <Card padding="xl" radius="lg" elevation="md">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-medium text-ink-900">대상</h2>
        {isOwner && (
          <button onClick={openAdd} className="text-sm text-brand-600 hover:text-brand-800">
            + 추가
          </button>
        )}
      </div>
      <p className="text-xs text-ink-400 mb-4">현장 내 대상(설비·시설 등) 목록. 사진 촬영 대상 지정에 사용됩니다.</p>

      {/* ②-c2 — 「설비 검토 완료」: 법정 설비가 0대일 때 «해당 없음»인지 «아직 안 넣음»인지를 가르는 사업장의 선언 */}
      {legalVisible && legal && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-card bg-ink-50 px-3 py-2" data-testid="equipment-review">
          <span className="text-xs text-ink-700">
            {legal.equipmentReviewedAt
              ? `설비 검토 완료 · ${formatDateISO(new Date(legal.equipmentReviewedAt))}`
              : '법정 설비(안전검사 대상) 검토 전 — 기종을 다 넣었으면 검토 완료를 표시하세요.'}
          </span>
          {isOwner && (
            <button
              type="button"
              onClick={toggleReview}
              disabled={isTogglingReview}
              className="ml-auto text-xs text-brand-600 hover:text-brand-800 disabled:opacity-50"
            >
              {legal.equipmentReviewedAt ? '검토 완료 해제' : '설비 검토 완료'}
            </button>
          )}
          {reviewError && <p className="w-full text-xs text-red-600">{reviewError}</p>}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
        </div>
      ) : activeEquipment.length === 0 ? (
        <div className="text-center py-6">
          <svg className="mx-auto h-8 w-8 text-ink-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="mt-2 text-sm text-ink-500">등록된 대상이 없습니다.</p>
          {isOwner && (
            <button onClick={openAdd} className="mt-2 text-sm text-brand-600 hover:text-brand-800">
              대상 추가하기
            </button>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {activeEquipment.map((eq) => (
            <li
              key={eq.id}
              className="py-3 flex items-center cursor-pointer hover:bg-surface-hover -mx-2 px-2 rounded-card transition-colors"
              onClick={() => setSelectedEquipment(eq)}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-ink-900">{eq.name}</span>
                  {eq.managementNumber && (
                    <Badge tone="slate" shape="rounded">
                      {eq.managementNumber}
                    </Badge>
                  )}
                  {legalVisible && (
                    <RegulatedEquipmentBadges
                      cls={classOf(eq.equipmentKindCode)}
                      values={eq.specValues}
                      external={{ facts: eq.externalFacts, notes: eq.externalFactNotes }}
                    />
                  )}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500 flex-wrap">
                  {eq.processType && <span>{eq.processType}</span>}
                  {eq.processType && (eq.currentLocation || eq.currentDetailLocation) && <span>·</span>}
                  {(eq.currentLocation || eq.currentDetailLocation) && (
                    <span className="flex items-center gap-0.5">
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {[eq.currentLocation?.name, eq.currentDetailLocation?.name].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {!eq.processType && !eq.currentLocation && !eq.currentDetailLocation && <span className="text-ink-400">-</span>}
                </div>
              </div>
              <ChevronRightIcon className="flex-shrink-0 h-5 w-5 text-ink-400 ml-2" />
            </li>
          ))}
        </ul>
      )}

      {/* 상세 모달 */}
      <Modal
        open={!!selectedEquipment}
        onClose={() => setSelectedEquipment(null)}
        title={selectedEquipment?.name}
        size="md"
        footer={
          selectedEquipment &&
          (isOwner ? (
            <>
              {/* 비활성화는 되돌릴 수 있는 경고성 액션 — 주황 의미색을 유지한다. */}
              <Button
                variant="secondary"
                onClick={() => { setSelectedEquipment(null); setDeactivateTarget(selectedEquipment); }}
                className="text-orange-600 hover:bg-orange-50 hover:text-orange-700"
              >
                비활성화
              </Button>
              <Button variant="primary" onClick={() => openEdit(selectedEquipment)}>
                수정
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={() => setSelectedEquipment(null)}>
              닫기
            </Button>
          ))
        }
      >
        {selectedEquipment && (
          <>
            {selectedEquipment.managementNumber && (
              <p className="text-sm text-ink-500 mb-4">{selectedEquipment.managementNumber}</p>
            )}
            <dl className="space-y-3">
              <div>
                <dt className="text-xs font-medium text-ink-500">공종/공정</dt>
                <dd className="text-sm text-ink-900">{selectedEquipment.processType || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-ink-500">장소</dt>
                <dd className="text-sm text-ink-900">{selectedEquipment.currentLocation?.name || '-'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-ink-500">위치</dt>
                <dd className="text-sm text-ink-900">{selectedEquipment.currentDetailLocation?.name || '-'}</dd>
              </div>
              {legalVisible && legal && (
                <div>
                  <dt className="text-xs font-medium text-ink-500">법정 설비 분류</dt>
                  <dd className="text-sm text-ink-900">
                    {classOf(selectedEquipment.equipmentKindCode) ? (
                      <>
                        <span>
                          {classOf(selectedEquipment.equipmentKindCode)?.name}
                          {selectedEquipment.installedAt ? ` · 설치 ${toDateInput(selectedEquipment.installedAt)}` : ''}
                        </span>
                        {(selectedEquipment.exhaustProcessIds?.length ?? 0) > 0 && (
                          <p className="mt-0.5 text-xs text-ink-500">
                            배기 공정:{' '}
                            {selectedEquipment.exhaustProcessIds!
                              .map((id) => legal.processes.find((p) => p.id === id)?.name ?? id)
                              .join(' · ')}
                          </p>
                        )}
                        <div className="mt-1">
                          <RegulatedEquipmentBadges
                            cls={classOf(selectedEquipment.equipmentKindCode)}
                            values={selectedEquipment.specValues}
                            external={{ facts: selectedEquipment.externalFacts, notes: selectedEquipment.externalFactNotes }}
                            showDetail
                          />
                        </div>
                      </>
                    ) : (
                      <span className="text-ink-500">법정 안전검사 대상인지 보려면 기종을 고르세요.</span>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </>
        )}
      </Modal>

      {/* 추가/수정 모달 — 저장 중에는 닫는 길 전부를 막는다(종전 오버레이의 `!isSubmitting` 가드와 같은 축).
          본문 스크롤(base 기본)을 그대로 받는다: 세 SearchableSelect의 목록은 body로 포탈되는
          고정 배치라 본문 경계에 잘리지 않는다. */}
      <Modal
        open={showModal}
        onClose={resetForm}
        title={editingEquipment ? '대상 수정' : '새 대상 추가'}
        size="sm"
        initialFocusRef={nameInputRef}
        closeOnOverlay={!isSubmitting}
        closeOnEsc={!isSubmitting}
        showCloseButton={!isSubmitting}
        closeLocked={isSubmitting}
        footer={
          <>
            {/* 저장이 왜 막혔는지 보이지 않으면 "버튼이 죽었다"로 읽힌다 — 이유를 같이 띄운다. */}
            {editingEquipment && (
              <DirtyHint isDirty={!isUnchanged} className="mr-auto self-center text-sm" />
            )}
            <Button variant="secondary" onClick={resetForm} disabled={isSubmitting}>
              취소
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!formName.trim() || isSubmitting || isUnchanged}
            >
              {isSubmitting ? '저장 중...' : editingEquipment ? '수정' : '추가'}
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-3 rounded-card bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              대상명 <span className="text-red-500">*</span>
            </label>
            <input
              ref={nameInputRef}
              type="text"
              placeholder="예: 프레스기, 지게차"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">관리번호</label>
            <input
              type="text"
              placeholder="예: 1호차, EQ-2026-001"
              value={formManagementNumber}
              onChange={(e) => setFormManagementNumber(e.target.value)}
              className="w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <SearchableSelect
            label="공종/공정"
            value={formProcessType}
            onChange={(v) => setFormProcessType(v)}
            options={processTypeOptions}
            placeholder="공종/공정 선택"
            onAdd={async (name) => {
              const created = await createSiteProcessType(companyId, siteId, { name });
              createdProcessTypeNamesRef.current.set(created.id, created.name ?? name);
              queryClient.invalidateQueries({ queryKey: ['site-process-types', companyId, siteId] });
              setFormProcessType(created.id);
              return created.id;
            }}
            sheetMode={!isDesktop}
          />
          <SearchableSelect
            label="장소"
            value={formLocationId}
            onChange={(v) => setFormLocationId(v)}
            options={activeLocations}
            placeholder="장소 선택"
            onAdd={async (name) => {
              const created = await createSiteLocation(companyId, siteId, { name });
              queryClient.invalidateQueries({ queryKey: ['site-locations', companyId, siteId] });
              return created.id;
            }}
            sheetMode={!isDesktop}
          />
          <SearchableSelect
            label="위치"
            value={formDetailLocationId}
            onChange={(v) => setFormDetailLocationId(v)}
            options={activeDetailLocations}
            placeholder="위치 선택"
            onAdd={async (name) => {
              const created = await createSiteDetailLocation(companyId, siteId, { name });
              queryClient.invalidateQueries({ queryKey: ['site-detail-locations', companyId, siteId] });
              return created.id;
            }}
            sheetMode={!isDesktop}
          />
          {legalVisible && legal && (
            <RegulatedEquipmentFields
              classes={legalClasses}
              kindCode={formKindCode}
              specValues={formSpecValues}
              installedDate={formInstalledDate}
              processes={legal.processes}
              exhaustProcessIds={formExhaustProcessIds}
              external={editingEquipment ? { facts: editingEquipment.externalFacts, notes: editingEquipment.externalFactNotes } : undefined}
              onKindChange={(code) => {
                // 기종을 바꾸면 규격 값·배기 공정을 비운다(서버와 같은 처분 — 다른 기종의 같은 키가 다른 뜻으로 남지 않게)
                if (code !== formKindCode) {
                  setFormSpecValues({});
                  setFormExhaustProcessIds([]);
                }
                setFormKindCode(code);
              }}
              onSpecChange={setFormSpecValues}
              onInstalledDateChange={setFormInstalledDate}
              onExhaustProcessChange={setFormExhaustProcessIds}
            />
          )}
        </div>
      </Modal>

      {/* 비활성화 확인 모달 — 처리 중에는 닫는 길을 막는다(종전엔 취소 버튼만 disabled였다) */}
      <Modal
        open={!!deactivateTarget}
        onClose={closeDeactivate}
        title="대상 비활성화"
        size="sm"
        closeOnOverlay={!isDeactivating}
        closeOnEsc={!isDeactivating}
        showCloseButton={!isDeactivating}
        closeLocked={isDeactivating}
        footer={
          <>
            <Button variant="secondary" onClick={closeDeactivate} disabled={isDeactivating}>
              취소
            </Button>
            {/* 비활성화 = 되돌릴 수 있는 경고성 액션 → warning(파괴적 danger와 구분). */}
            <Button variant="warning" onClick={handleDeactivate} disabled={isDeactivating}>
              {isDeactivating ? '처리 중...' : '비활성화'}
            </Button>
          </>
        }
      >
        {deactivateTarget && (
          <p className="text-sm text-ink-500 mb-4">
            <strong>{deactivateTarget.name}</strong>{objectParticleOf(deactivateTarget.name)} 비활성화하시겠습니까?
            <br />
            기존 사진 데이터는 유지되며, 새 점검에서 선택할 수 없게 됩니다.
          </p>
        )}
        {deactivateError && (
          <p className="text-sm text-red-600 mb-3">{deactivateError}</p>
        )}
      </Modal>
    </Card>
  );
}
